import { useEffect, useState } from "react";
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, FormField, FormTextarea } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useCreateProgram } from "@/api/hooks/useClassRegistration";

interface ProgramFormDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Create-program form — mirrors features/memberships/MemberFormDialog.tsx's
 * plain-useState admin-dialog convention (no react-hook-form needed for a
 * two-field form). Programs are only ever soft-deactivated
 * (PATCH .../programs/:id { active }), never deleted, so there's no
 * delete affordance here. */
export function ProgramFormDialog({ open, onClose }: ProgramFormDialogProps) {
  const createProgram = useCreateProgram();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
    }
  }, [open]);

  const canSubmit = !!name.trim() && !createProgram.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await createProgram.mutateAsync({ name: name.trim(), description: description.trim() || null });
      toastSuccess("Program added");
      onClose();
    } catch (err) {
      toastError("Couldn't add program", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="New program">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>New program</DialogHeader>
        <DialogBody className="space-y-4">
          <FormField
            label="Name"
            required
            placeholder="e.g. Lore Keepers"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormTextarea
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createProgram.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {createProgram.isPending ? "Adding…" : "Add program"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
